import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchTDSRates,
  fetchTdsSections,
  TaxLedgerMapEntry,
  TDSRate,
  TdsSectionType,
} from "../../../../services/api";
import { TdsRateForm } from "./TdsRateForm";
import { AddRateButton, RateRowMenu } from "./rateTableParts";
import {
  byEraThenName,
  FRESH_ROW_CLASS,
  isCodeLike,
  oldSectionFor,
  sectionLabelFor,
  TDS_COLUMNS_LEDGER,
} from "./tdsTable";

/**
 * Which form this panel has open, if any.
 *
 * ONE AT A TIME. Both forms write to the list above them and both take the whole
 * panel's width, so opening Edit puts the add form away and the other way round —
 * master-admin's OpenForm, and the reason it is one piece of state rather than an
 * `adding` flag beside an `editing` row.
 */
type OpenForm = { mode: "add" } | { mode: "edit"; rate: TDSRate };

export interface TdsRatesTableProps {
  /** Bumped by the page after a Zoho sync lands, to pull the new rows in. */
  reloadToken?: number;
  /**
   * tax_id → the account that rate's tax lands in, read live by the page. Null
   * while it is in flight or after it failed; either way the column reads "—"
   * and NOTHING HERE WAITS ON IT.
   */
  ledgerMap?: Record<string, TaxLedgerMapEntry> | null;
  /** Counts for the panel heading, which the page owns. */
  onCounts?: (counts: { active: number; expired: number }) => void;
  /** A rate was written to Zoho — the ledger map the page holds is now stale. */
  onZohoWrite?: () => void;
}

/**
 * TDS rates as a flat table, shaped like Zoho's own Income TDS Rates screen and
 * master-admin's onboarding review.
 *
 * ONE list, not an active section and an expired section. Expired rates are not a
 * footnote to be folded away: a back-dated bill is given the rate that applied at
 * the time, so a CA reading this needs to see them. They sort last and say so in
 * the status column, which is the only thing worth differing from Zoho on — with
 * fifteen of sixteen expired, the one that still works should not be somewhere in
 * the middle.
 *
 * Five columns and a menu. Threshold, deductee and AI context are curated here
 * but they do not belong in the register: a CA scans this to find a rate, and
 * every column that is not part of identifying one is a column read past. They
 * live in the form, which is where they are set.
 *
 * THE PAYABLE LEDGER IS THE ONE COLUMN NOT HELD LOCALLY. `tds_rates` stores no
 * account id and is not gaining one — see fetchTaxLedgerMap. It is also the one
 * column that can be blank on a row that is otherwise complete.
 */
export const TdsRatesTable: React.FC<TdsRatesTableProps> = ({
  reloadToken = 0,
  ledgerMap = null,
  onCounts,
  onZohoWrite,
}) => {
  const [rates, setRates] = useState<TDSRate[]>([]);
  // Act 2025 only. A new rate created today sits under the Act in force, and the
  // catalogue's old_section_no carries the 1961 number for the "was 194H" line,
  // so nothing is lost by not offering the retired vocabulary.
  const [sections, setSections] = useState<TdsSectionType[] | null>(null);
  const [sectionError, setSectionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState<OpenForm | null>(null);
  /** The Zoho tax id of the rate just created, for the one glance that looks. */
  const [freshId, setFreshId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchTDSRates();
      setRates(result.data.tds_rates);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load TDS rates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  // Seeded reference data, so it is fetched once and left out of the reload path.
  // A failure costs the "was 194H" line and gates the add button — which says so
  // beside itself rather than as a page error, because the table is still right.
  useEffect(() => {
    fetchTdsSections(2)
      .then(setSections)
      .catch((err) =>
        setSectionError(
          err instanceof Error
            ? `Could not read the section list: ${err.message}`
            : "Could not read the section list.",
        ),
      );
  }, []);

  const sectionIndex = useMemo(
    () => new Map((sections ?? []).map((s) => [s.tax_specific_type, s])),
    [sections],
  );

  const sorted = useMemo(() => [...rates].sort(byEraThenName), [rates]);
  const activeCount = rates.filter((r) => !r.is_expired).length;

  useEffect(() => {
    onCounts?.({ active: activeCount, expired: rates.length - activeCount });
  }, [onCounts, activeCount, rates.length]);

  const handleSaved = (message: string | null, createdTaxId: string | null) => {
    setNotice(message);
    setFreshId(createdTaxId);
    setOpenForm(null);
    load();
    // Both paths can have touched Zoho — a create always does, and a curation
    // edit is cheap to be wrong about. The page re-reads the ledger map either
    // way rather than reasoning about which.
    onZohoWrite?.();
  };

  return (
    <div className="flex w-full flex-col items-start gap-3">
      {notice && (
        <div className="flex w-full items-start rounded-lg border border-solid border-green-200 bg-green-50 p-3">
          <span className="text-body font-body text-green-800">{notice}</span>
        </div>
      )}
      {error && (
        <div className="flex w-full items-start rounded-lg border border-solid border-red-200 bg-red-50 p-3">
          <span className="text-body font-body text-red-600">{error}</span>
        </div>
      )}

      {rates.length === 0 ? (
        <span className="text-body font-body text-subtext-color">
          {loading ? "Loading…" : "No TDS rates yet. They arrive with the sync."}
        </span>
      ) : (
        <div className="flex w-full flex-col">
          <div
            className={`hidden gap-x-4 border-b border-solid border-neutral-border pb-1.5 text-caption-bold font-caption-bold uppercase text-subtext-color sm:grid ${TDS_COLUMNS_LEDGER}`}
          >
            <span>Section</span>
            <span>Nature of payment</span>
            <span className="text-right">Rate</span>
            <span>Payable ledger</span>
            <span>Status</span>
            <span />
          </div>

          {sorted.map((rate) => {
            const label = sectionLabelFor(rate, sectionIndex);
            const wasSection = oldSectionFor(rate, sectionIndex);
            const payable =
              ledgerMap?.[rate.tds_tax_id]?.payable_account_name ?? null;
            const fresh = freshId !== null && rate.tds_tax_id === freshId;

            return (
              <div key={rate.id} className={fresh ? FRESH_ROW_CLASS : undefined}>
                <div
                  className={`grid items-baseline gap-x-4 gap-y-1 border-b border-solid border-neutral-border py-2.5 ${
                    fresh ? "border-b-0 px-2" : ""
                  } ${TDS_COLUMNS_LEDGER}`}
                >
                  <span className="flex min-w-0 flex-col">
                    <span
                      className={`break-words text-body font-body text-default-font ${
                        isCodeLike(label) ? "font-mono" : ""
                      }`}
                    >
                      {label}
                    </span>
                    {wasSection && (
                      <span className="font-mono text-caption text-subtext-color">
                        was {wasSection}
                      </span>
                    )}
                  </span>
                  <span className="min-w-0 break-words text-body font-body text-default-font">
                    {rate.tds_tax_name}
                  </span>
                  <span className="text-body font-body text-default-font sm:text-right">
                    {rate.tds_tax_percentage}%
                  </span>
                  {/* Read live from Zoho and never stored, so a dash here means
                      "not read yet", not "no ledger". */}
                  <span
                    className={`min-w-0 break-words text-body font-body ${
                      payable === null
                        ? "text-subtext-color"
                        : "text-default-font"
                    }`}
                  >
                    {payable ?? "—"}
                  </span>
                  {/* Zoho paints the word and nothing else. Expired is red
                      because picking one on a current bill is a mistake, not a
                      preference. */}
                  <span
                    className={`text-body font-body ${
                      rate.is_expired ? "text-red-600" : "text-subtext-color"
                    }`}
                  >
                    {rate.is_expired ? "Expired" : "Active"}
                  </span>
                  <RateRowMenu
                    items={[
                      {
                        label: "Edit",
                        onSelect: () => setOpenForm({ mode: "edit", rate }),
                      },
                    ]}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* The add button, or whichever form is open in its place. Bottom of the
          panel under a divider, master-admin's placement: one per panel, because
          the two taxes take different section vocabularies and a shared button
          would have to ask which one first. */}
      {openForm === null ? (
        <AddRateButton
          label="TDS"
          loaded={sections !== null}
          error={sectionError}
          onOpen={() => setOpenForm({ mode: "add" })}
        />
      ) : (
        <div className="flex w-full flex-col border-t border-solid border-neutral-border pt-3">
          <TdsRateForm
            // Remounted per rate, so choosing Edit on a second row starts from
            // that row rather than from what was typed into the first.
            key={openForm.mode === "edit" ? openForm.rate.id : "add"}
            rate={openForm.mode === "edit" ? openForm.rate : null}
            sections={sections ?? []}
            onSaved={handleSaved}
            onCancel={() => setOpenForm(null)}
          />
        </div>
      )}
    </div>
  );
};
